/**
 * React hook for managing property motivations (manual + inferred)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface PropertyMotivations {
  property_id: number;
  manual_motivations: string[];
  inferred_motivations: { signal: string; confidence: number; source?: string; value?: string }[];
  motivation_level: 'high' | 'medium' | 'low';
  updated_at: string | null;
}

export function usePropertyMotivations(propertyId: number | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<PropertyMotivations>({
    queryKey: ['property-motivations', propertyId],
    queryFn: async () => {
      const response = await fetch(`/api/properties/${propertyId}/motivations`);
      if (!response.ok) {
        throw new Error('Failed to fetch property motivations');
      }
      return response.json();
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const mutation = useMutation({
    mutationFn: async (motivations: string[]) => {
      const response = await fetch(`/api/properties/${propertyId}/motivations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual_motivations: motivations }),
      });
      if (!response.ok) {
        throw new Error('Failed to update property motivations');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // Update the cache immediately
      queryClient.setQueryData(['property-motivations', propertyId], data);
      // Also invalidate playbook suggestions to refresh with new context
      queryClient.invalidateQueries({
        queryKey: ['playbook', 'suggestions'],
      });
    },
  });

  return {
    motivations: query.data,
    isLoading: query.isLoading,
    error: query.error,
    updateMotivations: mutation.mutate,
    isUpdating: mutation.isPending,
  };
}
